import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/shared/api/http';
import type { FileRef, PagedResult, UserRef } from '@/shared/api/types';

/* ------------------------------------------------------------------ wire types (docs/modules/webforms.md) */

export type FormLanguage = 'ru' | 'en' | 'uk';
export const FORM_LANGUAGES: readonly FormLanguage[] = ['ru', 'en', 'uk'];

/** A text in the languages of a form. */
export type LocalizedText = Partial<Record<FormLanguage, string>>;

export type FieldType =
  | 'text'
  | 'textarea'
  | 'email'
  | 'phone'
  | 'number'
  | 'date'
  | 'select'
  | 'radio'
  | 'checkbox'
  | 'file'
  | 'consent'
  | 'hidden'
  | 'section'
  | 'page';

export const FIELD_TYPES: readonly FieldType[] = [
  'text',
  'textarea',
  'email',
  'phone',
  'number',
  'date',
  'select',
  'radio',
  'checkbox',
  'file',
  'consent',
  'hidden',
  'section',
  'page',
];

export type FieldWidth = 'full' | 'half';
export type TextPattern = 'none' | 'letters' | 'digits' | 'custom';
export type DateRange = 'any' | 'future' | 'past';
export type ColorScheme = 'light' | 'dark' | 'auto';
export type ThemeRadius = 'none' | 'small' | 'medium' | 'large';
export type FormLayout = 'single' | 'twoColumns';
export type SuccessMode = 'message' | 'redirect';
export type ResponsibleMode = 'single' | 'queue';
export type SubmissionStatus = 'pending' | 'created' | 'attached' | 'error' | 'unconfirmed';
export const SUBMISSION_STATUSES: readonly SubmissionStatus[] = ['pending', 'created', 'attached', 'error', 'unconfirmed'];

export type LogicMatch = 'all' | 'any';
export type LogicOperator = 'is' | 'isNot' | 'contains' | 'notContains' | 'oneOf' | 'noneOf' | 'empty' | 'filled';
export const LOGIC_OPERATORS: readonly LogicOperator[] = ['is', 'isNot', 'contains', 'notContains', 'oneOf', 'noneOf', 'empty', 'filled'];

/** A comparison of another item's answer (option items: option ids). */
export interface LogicCondition {
  field: string;
  operator: LogicOperator;
  values: string[];
}

/** Conditions and nested groups combined with AND (`all`) or OR (`any`). */
export interface LogicGroup {
  match: LogicMatch;
  conditions: LogicCondition[];
  groups: LogicGroup[];
}

/** When an item is shown (only when `show` holds), hidden (when `hide` holds; wins over `show`) or required (when `require` holds). */
export interface FieldLogic {
  show: LogicGroup | null;
  hide: LogicGroup | null;
  require: LogicGroup | null;
}

export type CaptchaMode = 'default' | 'on' | 'off';

export interface FieldOption {
  id: string;
  label: LocalizedText;
  /** The value of the option in the CRM (the option id of a CRM list field); null: the label. */
  crmValue: string | null;
}

export interface FieldValidation {
  minLength: number | null;
  maxLength: number | null;
  min: number | null;
  max: number | null;
  integer: boolean;
  pattern: TextPattern;
  regex: string | null;
  patternMessage: LocalizedText;
  dateRange: DateRange;
}

export interface FileRules {
  maxSizeMb: number;
  maxFiles: number;
  /** Extensions without the dot; empty: documents, images and archives. */
  accept: string[];
}

export interface FormField {
  id: string;
  type: FieldType;
  label: LocalizedText;
  placeholder: LocalizedText;
  help: LocalizedText;
  required: boolean;
  width: FieldWidth;
  /** A standard request field (`name`, `phone`…) or a lead custom field (`cf_…`); null: into the comment. */
  crmField: string | null;
  urlParam: string | null;
  defaultValue: string | null;
  validation: FieldValidation;
  options: FieldOption[];
  multiple: boolean;
  file: FileRules;
  consentText: LocalizedText;
  consentUrl: string | null;
  /** Conditional logic (null: always shown, as the item says). */
  logic: FieldLogic | null;
}

export interface FormTheme {
  colorScheme: ColorScheme;
  accentColor: string;
  backgroundColor: string | null;
  radius: ThemeRadius;
  layout: FormLayout;
  showTitle: boolean;
}

export interface FormDefinition {
  languages: FormLanguage[];
  defaultLanguage: FormLanguage;
  title: LocalizedText;
  description: LocalizedText;
  submitText: LocalizedText;
  fields: FormField[];
  success: { mode: SuccessMode; message: LocalizedText; redirectUrl: string | null; redirectDelay: number };
  theme: FormTheme;
  responsible: { mode: ResponsibleMode; userIds: string[] };
  /** Who else hears of a submission (the responsible person gets the CRM's notification about the lead). */
  notifications: { userIds: string[] };
  /** The proof-of-work captcha: this form's setting (`default`: the portal's) and whether a question may replace the puzzle. */
  captcha: { mode: CaptchaMode; allowManual: boolean };
  /**
   * Settings of web forms 2.0 (docs/modules/webforms.md): what the form creates in the CRM, UTM tracking, the e-mails to the visitor,
   * double opt-in, allowed sites and the automation. The builder has no editor for them yet (they are set through the API); it keeps
   * them as they are when it saves, and forms saved before them have none.
   */
  target?: FormTarget;
  tracking?: { defaultUtm: Record<string, string>; utmFields: Record<string, string> };
  autoReply?: FormMail & { enabled: boolean };
  optIn?: FormMail & { enabled: boolean; expiryHours: number; pendingMessage: LocalizedText; confirmedMessage: LocalizedText };
  embedding?: { allowedDomains: string[] };
  automation?: {
    conditions: { id: string; name: string; rule: LogicGroup }[];
    webhook: { enabled: boolean; endpointId: string | null; path: string | null; conditionId: string | null };
  };
}

/** What a form creates in the CRM: a lead (the default), a deal or an element of an entity type. */
export interface FormTarget {
  kind: 'lead' | 'deal' | 'item';
  pipelineId: string | null;
  stageId: string | null;
  typeId: string | null;
  sourceId: string | null;
}

/** An e-mail to the visitor: a template per language with placeholders for the answers. */
export interface FormMail {
  emailFieldId: string | null;
  subject: LocalizedText;
  body: LocalizedText;
}

export interface StatsTotals {
  views: number;
  starts: number;
  submissions: number;
  blocked: number;
  /** Submissions per view (0–1), null without views. */
  conversion: number | null;
}

export interface FormListItem {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  fieldCount: number;
  languages: FormLanguage[];
  submissionCount: number;
  lastSubmissionAt: string | null;
  last30Days: StatsTotals;
  createdAt: string;
  updatedAt: string;
  updatedBy: UserRef | null;
}

export interface FormDetail {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  definition: FormDefinition;
  version: string;
  submissionCount: number;
  lastSubmissionAt: string | null;
  createdAt: string;
  createdBy: UserRef | null;
  updatedAt: string;
  updatedBy: UserRef | null;
  /** The people the form names, by id. */
  users: Record<string, UserRef>;
  canManage: boolean;
  /** Whether the caller sees every submission (administrators and the people the form names); else only those given to them. */
  seesAllSubmissions: boolean;
}

export interface WebformsMe {
  role: 'none' | 'viewer' | 'manager' | 'admin';
  canManage: boolean;
  timeZone: string;
  /** Whether forms set to `default` ask for the captcha (`Webforms:Captcha:DefaultEnabled`). */
  captchaDefault: boolean;
}

export interface CrmCustomField {
  code: string;
  label: string;
  type: 'string' | 'number' | 'money' | 'date' | 'list' | 'boolean' | 'user';
  isMultiple: boolean;
  isRequired: boolean;
  options: { id: string; label: string }[];
}

export interface CrmFields {
  standard: string[];
  custom: CrmCustomField[];
  /** The lead source the CRM's intake settings give web form requests (null: none chosen). */
  leadSource: string | null;
}

export interface StatsDay {
  day: string;
  views: number;
  starts: number;
  submissions: number;
  blocked: number;
}

export interface FormStats {
  from: string;
  to: string;
  totals: StatsTotals;
  allTime: StatsTotals;
  days: StatsDay[];
}

export interface SubmissionListItem {
  id: string;
  formId: string;
  number: number;
  createdAt: string;
  personName: string | null;
  email: string | null;
  phone: string | null;
  status: SubmissionStatus;
  error: string | null;
  responsible: UserRef | null;
  leadId: string | null;
  dealId: string | null;
  contactId: string | null;
  language: FormLanguage;
  itemId?: string | null;
  /** The viewer may send it to the CRM again (a refusal, or no answer for a while). */
  canRetry?: boolean;
}

export interface SubmissionValue {
  fieldId: string;
  type: FieldType;
  label: string;
  value: string;
  values: string[] | null;
  crmField: string | null;
}

export interface ConsentRecord {
  fieldId: string;
  label: string;
  text: string | null;
  url: string | null;
  accepted: boolean;
}

export interface CrmRecordRef {
  entityType: 'lead' | 'deal' | 'contact' | 'company' | 'item';
  id: string;
  title: string;
  url: string;
  responsibleId: string;
  responsibleName: string | null;
  status: string | null;
  isOpen: boolean;
}

export interface SubmissionDetail {
  id: string;
  formId: string;
  formName: string;
  number: number;
  createdAt: string;
  language: FormLanguage;
  values: SubmissionValue[];
  files: { fieldId: string; file: FileRef }[];
  consents: ConsentRecord[];
  pageUrl: string | null;
  referrer: string | null;
  utm: Record<string, string>;
  ipAddress: string | null;
  userAgent: string | null;
  status: SubmissionStatus;
  error: string | null;
  attempts: number;
  processedAt: string | null;
  assignedTo: UserRef | null;
  crmResponsible: UserRef | null;
  crmRecords: CrmRecordRef[];
  hiddenRecords: number;
  canRetry: boolean;
  canDelete: boolean;
  /** Double opt-in: when the visitor confirmed, and until when the link in the e-mail works (while it waits). */
  confirmedAt?: string | null;
  confirmationExpiresAt?: string | null;
  /** The e-mail to the visitor: pending, sent, skipped or failed (null: the form sends none). */
  autoReplyStatus?: 'pending' | 'sent' | 'skipped' | 'failed' | null;
  autoRetries?: number;
  /** When the worker sends a request the CRM has not answered again. */
  nextAutoRetryAt?: string | null;
}

export interface SubmissionQuery {
  status?: string;
  search?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

/* ------------------------------------------------------------------ public side (no sign-in) */

export interface PublicOption {
  id: string;
  label: string;
}

export interface PublicField {
  id: string;
  type: FieldType;
  label: string | null;
  placeholder: string | null;
  help: string | null;
  required: boolean;
  width: FieldWidth;
  urlParam: string | null;
  defaultValue: string | null;
  validation: {
    minLength: number | null;
    maxLength: number;
    min: number | null;
    max: number | null;
    integer: boolean;
    pattern: TextPattern;
    regex: string | null;
    patternMessage: string | null;
    dateRange: DateRange;
  };
  options: PublicOption[];
  multiple: boolean;
  file: FileRules | null;
  consentText: string | null;
  consentUrl: string | null;
  /** The browser's autofill hint (from the CRM mapping). */
  autocomplete: string | null;
  /** Conditional logic: the renderer evaluates it for the visitor, the server again on sending. */
  logic?: FieldLogic | null;
}

/** A form as visitors see it (one language). */
export interface PublicForm {
  code: string;
  language: FormLanguage;
  languages: FormLanguage[];
  title: string | null;
  description: string | null;
  submitText: string | null;
  fields: PublicField[];
  theme: FormTheme;
  token: string;
  minFillMs: number;
  /** The captcha the form asks for (null: none). */
  captcha?: { allowManual: boolean } | null;
}

/** A captcha challenge (`POST …/captcha`): a puzzle to solve or, with `kind: 'manual'`, a question to answer in text. */
export interface CaptchaChallenge {
  kind: 'pow' | 'manual';
  challenge: string;
  /** `pow`: the leading zero bits the hash of the solution needs. */
  bits: number;
  expiresInSeconds: number;
  question: string | null;
}

/** What a sending carries for the captcha. */
export interface CaptchaSolution {
  challenge: string;
  counter: number | null;
  answer: string | null;
}

export interface PublicUpload {
  token: string;
  name: string;
  size: number;
}

export interface PublicSubmitResult {
  message: string | null;
  redirectUrl: string | null;
  redirectDelay: number;
  /** Double opt-in: the submission waits for the visitor to confirm the link in the e-mail. */
  confirmationRequired?: boolean;
}

/** The page of the link in a double opt-in e-mail (`GET /api/webforms/public/confirm/{token}`). */
export interface PublicConfirmation {
  state: 'pending' | 'confirmed' | 'expired';
  code: string;
  language: FormLanguage;
  title: string | null;
  theme: FormTheme;
  expiresAt: string | null;
}

/** The answer to confirming (`POST …`): the message of the form and where to go next. */
export interface PublicConfirmed {
  state: 'confirmed';
  message: string | null;
  redirectUrl: string | null;
  redirectDelay: number;
}

/* ------------------------------------------------------------------ requests */

const base = '/webforms';

export const webformsApi = {
  me: (signal?: AbortSignal) => api.get<WebformsMe>(`${base}/me`, { signal }),
  crmFields: (signal?: AbortSignal) => api.get<CrmFields>(`${base}/crm-fields`, { signal }),
  forms: (search: string, signal?: AbortSignal) => api.get<FormListItem[]>(`${base}/forms`, { query: { search }, signal }),
  form: (id: string, signal?: AbortSignal) => api.get<FormDetail>(`${base}/forms/${encodeURIComponent(id)}`, { signal }),
  create: (body: { name: string; template?: string; language?: FormLanguage }) =>
    api.post<FormDetail>(`${base}/forms`, body),
  /** Saves the builder's draft; the switch is not part of it (`setActive` switches at once). */
  update: (id: string, body: { name: string; code: string; definition: FormDefinition; version: string }) =>
    api.put<FormDetail>(`${base}/forms/${encodeURIComponent(id)}`, body),
  setActive: (id: string, isActive: boolean) =>
    api.put<FormDetail>(`${base}/forms/${encodeURIComponent(id)}/active`, { isActive }),
  copy: (id: string) => api.post<FormDetail>(`${base}/forms/${encodeURIComponent(id)}/copy`, {}),
  remove: (id: string) => api.delete(`${base}/forms/${encodeURIComponent(id)}`),
  stats: (id: string, days: number, signal?: AbortSignal) =>
    api.get<FormStats>(`${base}/forms/${encodeURIComponent(id)}/stats`, { query: { days }, signal }),
  submissions: (formId: string, query: SubmissionQuery, signal?: AbortSignal) =>
    api.get<PagedResult<SubmissionListItem>>(`${base}/forms/${encodeURIComponent(formId)}/submissions`, {
      query: { ...query },
      signal,
    }),
  submission: (id: string, signal?: AbortSignal) =>
    api.get<SubmissionDetail>(`${base}/submissions/${encodeURIComponent(id)}`, { signal }),
  retry: (id: string) => api.post<SubmissionDetail>(`${base}/submissions/${encodeURIComponent(id)}/retry`, {}),
  removeSubmission: (id: string) => api.delete(`${base}/submissions/${encodeURIComponent(id)}`),
  /** The submissions (the list's filters) as CSV; `truncated` when there were more than the file holds. */
  exportCsv: async (formId: string, query: SubmissionQuery) => {
    let truncated = false;
    const body = await api.get<Blob | string>(`${base}/forms/${encodeURIComponent(formId)}/submissions/export`, {
      query: { ...query, page: undefined, pageSize: undefined },
      headers: { Accept: '*/*' },
      onResponse: (response) => {
        truncated = response.headers.get('X-Orbita-Truncated') === 'true';
      },
    });
    const blob =
      typeof body === 'string' ? new Blob(['﻿', body], { type: 'text/csv;charset=utf-8' }) : body;
    return { blob, truncated };
  },
};

/* ------------------------------------------------------------------ query keys and hooks */

export const webformsKeys = {
  all: ['webforms'] as const,
  me: () => ['webforms', 'me'] as const,
  crmFields: () => ['webforms', 'crm-fields'] as const,
  forms: (search = '') => ['webforms', 'forms', search] as const,
  form: (id: string) => ['webforms', 'form', id] as const,
  stats: (id: string, days: number) => ['webforms', 'stats', id, days] as const,
  submissions: (formId: string, query?: SubmissionQuery) =>
    query ? (['webforms', 'submissions', formId, query] as const) : (['webforms', 'submissions', formId] as const),
  submission: (id: string) => ['webforms', 'submission', id] as const,
};

export function useWebformsMe() {
  return useQuery({ queryKey: webformsKeys.me(), queryFn: ({ signal }) => webformsApi.me(signal), staleTime: 60_000 });
}

export function useCrmFields(enabled = true) {
  return useQuery({
    queryKey: webformsKeys.crmFields(),
    queryFn: ({ signal }) => webformsApi.crmFields(signal),
    staleTime: 60_000,
    enabled,
  });
}

export function useForms(search: string) {
  return useQuery({
    queryKey: webformsKeys.forms(search),
    queryFn: ({ signal }) => webformsApi.forms(search, signal),
    placeholderData: keepPreviousData,
  });
}

export function useForm(id: string) {
  return useQuery({ queryKey: webformsKeys.form(id), queryFn: ({ signal }) => webformsApi.form(id, signal) });
}

export function useFormStats(id: string, days: number) {
  return useQuery({
    queryKey: webformsKeys.stats(id, days),
    queryFn: ({ signal }) => webformsApi.stats(id, days, signal),
    placeholderData: keepPreviousData,
  });
}

export function useSubmissions(formId: string, query: SubmissionQuery) {
  return useQuery({
    queryKey: webformsKeys.submissions(formId, query),
    queryFn: ({ signal }) => webformsApi.submissions(formId, query, signal),
    placeholderData: keepPreviousData,
  });
}

export function useSubmission(id: string) {
  return useQuery({ queryKey: webformsKeys.submission(id), queryFn: ({ signal }) => webformsApi.submission(id, signal) });
}

/** Saves the builder's form; the cached form and the list follow. */
export function useSaveForm(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: Parameters<typeof webformsApi.update>[1]) => webformsApi.update(id, body),
    meta: { silent: true },
    onSuccess: (form) => {
      queryClient.setQueryData(webformsKeys.form(id), form);
      void queryClient.invalidateQueries({ queryKey: ['webforms', 'forms'] });
    },
  });
}

export function useSetFormActive() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) => webformsApi.setActive(id, isActive),
    onSuccess: (form) => {
      queryClient.setQueryData(webformsKeys.form(form.id), form);
      void queryClient.invalidateQueries({ queryKey: ['webforms', 'forms'] });
    },
  });
}

export function useInvalidateWebforms() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: webformsKeys.all });
}
